import { ArrowLeftIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import Modal from './Modal';
import FacingDirectionBadge from './FacingDirectionBadge';

interface FacingConfirmationDialogProps {
  isOpen: boolean;
  direction: 'left' | 'right';
  confidence: number;
  onConfirm: () => void;
  onSwitch: () => void;
  onClose: () => void;
}

/**
 * Modal dialog for confirming medium-confidence facing direction detection
 *
 * Used when the AI detection has medium confidence (50-85%).
 * Allows the user to confirm the detected direction or switch to the opposite.
 *
 * @example
 * ```tsx
 * <FacingConfirmationDialog
 *   isOpen={needsConfirmation}
 *   direction="left"
 *   confidence={0.72}
 *   onConfirm={() => confirmDirection('left')}
 *   onSwitch={() => confirmDirection('right')}
 *   onClose={() => reset()}
 * />
 * ```
 */
function FacingConfirmationDialog({
  isOpen,
  direction,
  confidence,
  onConfirm,
  onSwitch,
  onClose,
}: FacingConfirmationDialogProps) {
  const oppositeDirection = direction === 'left' ? 'right' : 'left';
  const OppositeIcon = oppositeDirection === 'left' ? ArrowLeftIcon : ArrowRightIcon;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Confirm Facing Direction"
      size="md"
    >
      <div className="space-y-4">
        {/* Explanation */}
        <p className="text-sm text-text-secondary">
          We detected that the athlete is facing{' '}
          <span className="font-medium text-text-primary">{direction}</span> with{' '}
          <span className="font-medium text-text-primary">{Math.round(confidence * 100)}%</span> confidence.
          Please confirm or switch to the other direction.
        </p>

        {/* Current detection display */}
        <div className="flex items-center justify-center py-4">
          <FacingDirectionBadge
            direction={direction}
            confidence={confidence}
            showConfidence={true}
          />
        </div>

        {/* Information box */}
        <div className="bg-info/10 border border-info/30 rounded-lg p-3">
          <p className="text-sm text-info">
            <span className="font-medium">Why this matters:</span> Facing direction
            affects how we compare the athlete's movement to the template. Selecting
            the correct direction ensures accurate form analysis.
          </p>
        </div>

        {/* Action buttons */}
        <div className="flex gap-3 pt-2">
          <button
            onClick={onConfirm}
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-accent hover:bg-accent-hover text-dark-base font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-accent/50 focus:ring-offset-2 focus:ring-offset-dark-base"
          >
            <ArrowLeftIcon
              className={direction === 'left' ? 'w-4 h-4' : 'hidden'}
              aria-hidden="true"
            />
            <ArrowRightIcon
              className={direction === 'right' ? 'w-4 h-4' : 'hidden'}
              aria-hidden="true"
            />
            Use Detected ({direction})
          </button>

          <button
            onClick={onSwitch}
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-dark-elevated-2 hover:bg-dark-card text-text-primary border border-border-subtle font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-border-strong/50 focus:ring-offset-2 focus:ring-offset-dark-base"
          >
            <OppositeIcon className="w-4 h-4" aria-hidden="true" />
            Switch to {oppositeDirection}
          </button>
        </div>

        {/* Cancel option */}
        <button
          onClick={onClose}
          className="w-full text-sm text-text-muted hover:text-text-secondary py-2 transition-colors"
        >
          Cancel
        </button>
      </div>
    </Modal>
  );
}

export default FacingConfirmationDialog;
